import React from 'react';
import { Link } from 'react-router-dom';
import {
  IconWithText,
  DataDefinitionList,
  Icon
} from '../../web.common';
import { v4 as uuidv4 } from 'uuid';
import ReactTooltip from 'react-tooltip';

function amountTwoDigits(amount) {
  if (amount === undefined || amount === null) {
    return '';
  }

  if (typeof (amount) !== 'number') {
    return '';
  }

  return amount.toFixed(2).toString();
}

function percentage(amountDecimal) {
  if (amountDecimal === undefined || amountDecimal === null) {
    return '';
  }

  if (typeof (amountDecimal) !== 'number') {
    return '';
  }

  return `${(amountDecimal * 100).toFixed(2).toString()} %`;
}

export const DisplayPercentage = ({ children: amount }) => {
  const { AsDecimal, AsPercentage } = amount;
  const valueDecimal = AsDecimal !== undefined || AsDecimal !== null
    ? percentage(AsDecimal) : null;
  const valuePercentage = AsPercentage !== undefined || AsPercentage !== null
    ? percentage(AsPercentage / 100) : null;

  return <span style={{ whiteSpace: 'nowrap' }}>{valueDecimal || valuePercentage || ''}</span>;
};

export const DisplayCurrencyAmount = ({ amount, currency, alignRight }) => {
  const classes = ['display-currency-amount'];

  if (alignRight) {
    classes.push('align-right');
  }

  return (
    <span style={{ textAlign: 'right !important' }} className={classes.join(' ')}>
      {amountTwoDigits(amount)}
      {' '}
      {currency || ''}
    </span>
  );
};

export const DisplayDate = ({ children, dateOnly, breakTime }) => {
  if (!children || children.indexOf('0001-01-01') === 0) {
    return <span className='display-date'>-</span>;
  }

  if (breakTime) {
    let dateAndTime = children ? children.split('+')[0].split('T') : null;
    if (!dateAndTime) {
      return '';
    }
    const [date, time] = dateAndTime;

    return <span className='display-date'>{date}<br />{time.substring(0,5)}</span>;
  }

  // TODO evaluate to use date formatting
  let formattedDate = children ? children.split('T').join(' ').split('+')[0] : '';

  if (dateOnly && formattedDate) {
    formattedDate = formattedDate.substring(0, 10);
  }
  else {
    formattedDate = formattedDate.substring(0, 19);
  }

  return <span className='display-date'>{formattedDate}</span>;
};

export const DisplayDateRange = ({ children: [dateFrom, dateTo], dateOnly }) => (
  <React.Fragment>
    <DisplayDate dateOnly={dateOnly}>{dateFrom}</DisplayDate>
    <span>&nbsp;-&nbsp;</span>
    <DisplayDate dateOnly={dateOnly}>{dateTo}</DisplayDate>
  </React.Fragment>
);

export const DisplayGuid = ({ children: id, baseAnchor, short }) => {
  if (!id) {
    return '';
  }

  const idDisplay = short
    ? `${id.substring(0, 5)}…${id.substring(31, 36)}`
    : id;
  const anchor = baseAnchor ? baseAnchor.replace(/^[./]+/g, '') : null;
  const content = anchor
    ? (
      <Link to={`/${anchor}/${id}`}>{idDisplay}</Link>
    ) : idDisplay;

  return (
    <span className='display-guid'>
      {content}
    </span>
  );
};

function getDisplayClass(mapping, status) {
  const supportedColors = ['red', 'green', 'blue', 'yellow', 'red', 'gray'];
  const defaultStatusClass = 'gray';

  if (!mapping) {
    return defaultStatusClass;
  }

  if (!Array.isArray(mapping)) {
    console.warn('Display status mapping should be an array');

    return defaultStatusClass;
  }

  const mappedStatus = mapping.filter(x => x.value.toLowerCase() === status.toLowerCase())[0];

  if (!mappedStatus) {
    console.warn(`Unable to find status with value ${status} among options`);

    return defaultStatusClass;
  }

  if (!mappedStatus.color) {
    console.warn(`Unable to find a color related to status ${status}`);

    return defaultStatusClass;
  }

  const color = mappedStatus.color.toLowerCase();

  if (supportedColors.indexOf(color) < 0) {
    console.warn(`color status ${color} is not supported`);

    return defaultStatusClass;
  }

  return color;
}

export const DisplayStatus = ({ children: statusInput, mapping }) => {
  const status = statusInput || 'ND';
  const statusClass = mapping ? getDisplayClass(mapping, status) : 'gray';

  return (<span className={`display-status display-status-${statusClass} tag`}>{status}</span>);
};

export const DisplayType = ({ children }) => <span className='display-type tag'>{children}</span>;

export const DisplayAddress = ({ children: fullAddress = {} }) => {
  if (typeof (fullAddress) !== 'object') {
    return '';
  }

  const {
    String: address = '',
    Location: location = {}
  } = fullAddress;

  const {
    Latitude: latitude,
    Longitude: longitude
  } = location;

  return (
    <span className='display-location'>
      {address}
      {
        latitude && longitude
          ? (
            <React.Fragment>
              <span> - </span>
              <span className='geolocation'>{`${latitude}, ${longitude}`}</span>
            </React.Fragment>
          ) : ''
      }
    </span>
  );
};

export const DisplayArea = ({ children: fullAddress = {} }) => {
  if (typeof (fullAddress) !== 'object') {
    return '';
  }

  const {
    Name: address = '',
    Position: location = {},
    AreaId: areaId = null
  } = fullAddress;

  const {
    Latitude: latitude,
    Longitude: longitude
  } = location;

  const areaLink = areaId ? (
    <Link to={`/area/${areaId}`}>{address}</Link>
  ) : address;

  return (
    <span className='display-location'>
      {areaLink}
      {
        latitude && longitude
          ? (
            <React.Fragment>
              <span> - </span>
              <span className='geolocation'>{`${latitude}, ${longitude}`}</span>
            </React.Fragment>
          ) : ''
      }
    </span>
  );
};

export const DisplayCategory = ({ children }) => <span className='display-category tag'>{children}</span>;

export const DisplayUser = ({ children: user }) => {
  const {
    Name,
    Email
  } = user;
  return (<span className='display-user'>{`${Name} - ${Email}`}</span>);
};

export const DisplayBoolean = ({ children: value }) => {
  const display = value
    ? <Icon className='true' iconName='check' />
    : <Icon className='false' iconName='times' />;

  return (<span className='display-boolean'>{display}</span>);
};

export const DisplayPrice = ({ children: price, details }) => {
  const {
    GrandTotal,
    Amount,
    CurrencyIsoCode,
    AmountMembership,
    AmountNonMembership,
    TaxAmount,
    DiscountAmount,
    ServiceFeeAmount,
    TaxIndex
  } = price;

  const priceDetails = {
    'Grand Total': amountTwoDigits(GrandTotal),
    Amount: amountTwoDigits(Amount),
    'Amount Membership': amountTwoDigits(AmountMembership),
    'Amount Non Membership': amountTwoDigits(AmountNonMembership),
    'Tax Amount': amountTwoDigits(TaxAmount),
    'Discount Amount': amountTwoDigits(DiscountAmount),
    'Service Fee Amount': amountTwoDigits(ServiceFeeAmount),
    'Tax Index': percentage(TaxIndex.AsDecimal)
  };

  const tooltipId = uuidv4();

  return (
    <span className='display-price'>

      {
        details !== 'inline' ? ''
          : (
            <DisplayCurrencyAmount amount={GrandTotal} currency={CurrencyIsoCode} />
          )
      }
      {
        details !== 'hover' ? ''
          : (
            <React.Fragment>
              <span data-tip data-for={tooltipId}>
                <DisplayCurrencyAmount amount={GrandTotal} currency={CurrencyIsoCode} />
                <Icon iconName='info-circle' />
              </span>

              <ReactTooltip id={tooltipId} place='bottom' type='light' effect='solid'>
                <div className='hover-price'>
                  <DataDefinitionList
                    listData={priceDetails}
                    classNames={['price-details-list']}
                  />
                </div>
              </ReactTooltip>

            </React.Fragment>
          )
      }
    </span>
  );
};

export const DisplayPaymentMethod = ({ children: { Name } }) => (
  <span className='display-payment-method'>
    <IconWithText
      iconName='credit-card'
      text={Name}
    />
  </span>
);